import { redirect } from "@tanstack/react-router";
import { can } from "@/lib/staff-roles";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Inbox, RefreshCw, Mail, Search, CalendarDays, ShoppingBag, Image as ImageIcon, MessageSquare } from "lucide-react";
import { LODGE_EMAIL } from "@/lib/contact-info";
import { BookingApprovalWizard } from "@/components/admin/BookingApprovalWizard";


export const Route = createFileRoute("/_authenticated/admin/inbox")({
  beforeLoad: ({ context }) => {
    if (!can(context.staffRole, "viewBookings")) {
      throw redirect({ to: "/admin/calendar" });
    }
  },
  head: () => ({
    meta: [
      { title: "Submissions Inbox — Coffman Cove Lodging" },
      { name: "description", content: "Internal inbox of every booking request, order, and photo submission." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminInbox,
});

type Kind = "booking" | "order" | "photo";

type Item = {
  id: string;
  kind: Kind;
  createdAt: string;
  name: string;
  email: string | null;
  phone?: string | null;
  status: string;
  summary: string;
  details: { label: string; value: string }[];
  /** Raw booking row, present on booking requests so staff can work them. */
  booking?: { id: string; status: string; arrival_date: string | null; departure_date: string | null };

};

const KIND_META: Record<Kind, { label: string; icon: typeof Mail }> = {
  booking: { label: "Booking request", icon: CalendarDays },
  order: { label: "Shop order", icon: ShoppingBag },
  photo: { label: "Photo submission", icon: ImageIcon },
};

function money(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function fmt(iso: string) {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function AdminInbox() {
  const { staffRole } = Route.useRouteContext();
  const canManage = can(staffRole, "manageBookings");
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [items, setItems] = useState<Item[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [kindFilter, setKindFilter] = useState<Kind | "all">("all");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bookings, orders, photos] = await Promise.all([
        supabase.from("booking_requests").select("*").order("created_at", { ascending: false }).limit(200),
        supabase.from("shop_orders").select("*").order("created_at", { ascending: false }).limit(200),
        supabase.from("gallery_submissions").select("*").order("created_at", { ascending: false }).limit(200),
      ]);

      const merged: Item[] = [];

      for (const b of bookings.data ?? []) {
        merged.push({
          id: `booking-${b.id}`,
          kind: "booking",
          booking: {
            id: b.id,
            status: b.status,
            arrival_date: b.arrival_date,
            departure_date: b.departure_date,
          },

          createdAt: b.created_at,
          name: b.name,
          email: b.email,
          phone: b.phone,
          status: b.status,
          summary: [b.package, b.arrival_date && `${b.arrival_date} → ${b.departure_date ?? "?"}`, b.party_size && `${b.party_size} anglers`]
            .filter(Boolean)
            .join(" · ") || "Date request",
          details: [
            { label: "Source", value: b.source },
            { label: "Package", value: b.package ?? "—" },
            { label: "Arrival", value: b.arrival_date ?? "—" },
            { label: "Departure", value: b.departure_date ?? "—" },
            { label: "Party size", value: b.party_size ? String(b.party_size) : "—" },
            { label: "Target species", value: b.target_species ?? "—" },
            { label: "Notes", value: b.notes ?? "—" },
          ],
        });
      }

      for (const o of orders.data ?? []) {
        const lines = Array.isArray(o.items)
          ? (o.items as { name?: string; quantity?: number; variant?: string }[])
              .map((i) => `${i.quantity ?? 1}× ${i.name ?? "item"}${i.variant ? ` (${i.variant})` : ""}`)
              .join(", ")
          : "";
        merged.push({
          id: `order-${o.id}`,
          kind: "order",
          createdAt: o.created_at,
          name: o.customer_name,
          email: o.email,
          phone: o.phone,
          status: o.status,
          summary: `${money(o.total_cents)} · ${lines || "order"}`,
          details: [
            { label: "Items", value: lines || "—" },
            { label: "Subtotal", value: money(o.subtotal_cents) },
            { label: "Shipping", value: money(o.shipping_cents) },
            { label: "Total", value: money(o.total_cents) },
            {
              label: "Ship to",
              value: [o.address_line1, o.address_line2, `${o.city}, ${o.state} ${o.postal_code}`].filter(Boolean).join(", "),
            },
            { label: "Notes", value: o.notes ?? "—" },
          ],
        });
      }

      for (const p of photos.data ?? []) {
        merged.push({
          id: `photo-${p.id}`,
          kind: "photo",
          createdAt: p.created_at,
          name: p.submitter_name,
          email: p.submitter_email,
          status: p.status,
          summary: p.caption ?? "Photo submission",
          details: [
            { label: "Caption", value: p.caption ?? "—" },
            { label: "Reviewer notes", value: p.reviewer_notes ?? "—" },
          ],
        });
      }

      merged.sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
      setItems(merged);
    } catch {
      toast.error("Could not load submissions");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    void (async () => {
      const { data: userRes } = await supabase.auth.getUser();
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userRes.user?.id ?? "")
        .eq("role", "admin");
      const ok = !!roles && roles.length > 0;
      setIsAdmin(ok);
      if (ok) void load();
    })();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (items ?? []).filter((i) => {
      if (kindFilter !== "all" && i.kind !== kindFilter) return false;
      if (!q) return true;
      return (
        i.name.toLowerCase().includes(q) ||
        (i.email ?? "").toLowerCase().includes(q) ||
        i.summary.toLowerCase().includes(q)
      );
    });
  }, [items, kindFilter, query]);

  const counts = useMemo(() => {
    const c = { all: items?.length ?? 0, booking: 0, order: 0, photo: 0 } as Record<Kind | "all", number>;
    for (const i of items ?? []) c[i.kind] += 1;
    return c;
  }, [items]);

  if (isAdmin === false) {
    return (
      <div className="mx-auto max-w-xl px-4 py-24 text-center">
        <h1 className="font-display text-2xl">Admins only</h1>
        <p className="mt-2 text-sm text-muted-foreground">This inbox is limited to lodge staff accounts.</p>
        <Link to="/admin" className="mt-6 inline-block text-sm underline">
          Sign in as admin
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-12">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[var(--color-ember)]">
            <Inbox size={18} aria-hidden />
            <span className="text-xs font-semibold uppercase tracking-[0.25em]">Staff inbox</span>
          </div>
          <h1 className="font-display text-3xl mt-2">Submissions</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Everything sent through the site. Copies are addressed to{" "}
            <a href={`mailto:${LODGE_EMAIL}`} className="underline">
              {LODGE_EMAIL}
            </a>
            .
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/admin/reviews"
            className="rounded-md border border-border px-3 py-2 text-sm hover:bg-muted"
          >
            Photo review queue
          </Link>
          <Link
            to="/admin/email-status"
            className="rounded-md border border-border px-3 py-2 text-sm hover:bg-muted"
          >
            Email status
          </Link>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-60"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} aria-hidden />
            Refresh
          </button>
        </div>
      </header>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {(["all", "booking", "order", "photo"] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setKindFilter(k)}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider ${
              kindFilter === k
                ? "border-[var(--color-ember)] bg-[var(--color-ember)] text-[var(--color-ember-foreground)]"
                : "border-border text-muted-foreground hover:bg-muted"
            }`}
          >
            {k === "all" ? "All" : KIND_META[k].label} ({counts[k]})
          </button>
        ))}
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, details"
            aria-label="Search submissions"
            className="w-64 rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm"
          />
        </div>
      </div>

      <div className="mt-6 divide-y divide-border rounded-xl border border-border bg-card">
        {items === null ? (
          <p className="p-6 text-sm text-muted-foreground">Loading submissions…</p>
        ) : filtered.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">No submissions match this view yet.</p>
        ) : (
          filtered.map((item) => {
            const Icon = KIND_META[item.kind].icon;
            const open = openId === item.id;
            return (
              <div key={item.id} className="p-4">
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : item.id)}
                  aria-expanded={open}
                  className="flex w-full items-start gap-3 text-left"
                >
                  <Icon size={18} className="mt-1 shrink-0 text-[var(--color-ember)]" aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{item.name}</span>
                      <span className="rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                        {KIND_META[item.kind].label}
                      </span>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wider">
                        {item.status}
                      </span>
                    </span>
                    <span className="mt-1 block truncate text-sm text-muted-foreground">{item.summary}</span>
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">{fmt(item.createdAt)}</span>
                </button>

                {open && (
                  <div className="mt-4 rounded-lg bg-muted/40 p-4">
                    <dl className="grid gap-3 sm:grid-cols-2">
                      {item.email && (
                        <div>
                          <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Email</dt>
                          <dd className="text-sm">
                            <a href={`mailto:${item.email}`} className="underline">
                              {item.email}
                            </a>
                          </dd>
                        </div>
                      )}
                      {item.phone && (
                        <div>
                          <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Phone</dt>
                          <dd className="text-sm">
                            <a href={`tel:${item.phone.replace(/[^\d+]/g, "")}`} className="underline">
                              {item.phone}
                            </a>
                          </dd>
                        </div>
                      )}
                      {item.details.map((d) => (
                        <div key={d.label} className="sm:col-span-2">
                          <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{d.label}</dt>
                          <dd className="whitespace-pre-wrap text-sm">{d.value}</dd>
                        </div>
                      ))}
                    </dl>
                    {item.booking && canManage && (
                      <BookingApprovalWizard booking={item.booking} onChanged={load} />
                    )}

                    {item.email && (
                      <a
                        href={`mailto:${item.email}?subject=${encodeURIComponent(
                          `Coffman Cove Lodging — your ${KIND_META[item.kind].label.toLowerCase()}`,
                        )}&cc=${LODGE_EMAIL}`}
                        className="mt-4 inline-flex items-center gap-2 rounded-md bg-[var(--color-ember)] px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)]"
                      >
                        <MessageSquare size={14} aria-hidden />
                        Reply
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
